import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Phone } from "lucide-react";
import { EASE } from "@/App";
import { villeParSlug, villesVoisines, slugify } from "@/data/villes";
import Header from "@/components/Header";
import QuoteForm from "@/components/QuoteForm";
import Footer from "@/components/Footer";

const IMAGES = [
  "/images/salle-de-bain-gris.png",
  "/images/salle-de-bain-bois.png",
  "/images/salle-de-bain-quartz.png",
];

const SERVICES = [
  "Démolition et préparation complète",
  "Plomberie et électricité",
  "Douche à l'italienne et céramique",
  "Bain autoportant et robinetterie",
  "Vanité quartz sur mesure",
  "Plancher et finitions",
];

export default function VillePage() {
  const { slug } = useParams();
  const entree = villeParSlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!entree) return;
    const titreOriginal = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const descOriginale = meta ? meta.getAttribute("content") : "";
    document.title = `Rénovation salle de bain ${entree.nom} | Salle de Bain Expert`;
    if (meta) {
      meta.setAttribute(
        "content",
        `Rénovation de salle de bain à ${entree.nom} (${entree.mrc}) — de A à Z : douche à l'italienne, céramique, vanité quartz, bain autoportant. Soumission gratuite : 438-985-1797. RBQ : Q:5881-6240-01.`
      );
    }
    return () => {
      document.title = titreOriginal;
      if (meta) meta.setAttribute("content", descOriginale);
    };
  }, [entree]);

  if (!entree) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <p className="uppercase tracking-[0.25em] text-xs text-[#D1BFAE]">
          Secteur introuvable
        </p>
        <h1 className="font-display text-3xl sm:text-4xl mt-6">
          Cette page n'existe pas
        </h1>
        <Link
          to="/"
          data-testid="ville-404-accueil"
          className="mt-8 text-sm text-[#D1BFAE] underline underline-offset-4"
        >
          Retour à l'accueil
        </Link>
      </div>
    );
  }

  const image = IMAGES[entree.index % IMAGES.length];
  const image2 = IMAGES[(entree.index + 1) % IMAGES.length];
  const voisines = villesVoisines(entree.mrc, entree.nom);

  return (
    <>
      <Header />
      <main>
        <section
          id="accueil"
          data-testid="ville-hero"
          className="relative min-h-[62vh] flex items-end overflow-hidden"
        >
          <img
            src={image}
            alt={`Rénovation de salle de bain à ${entree.nom} par Salle de Bain Expert`}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#5b676f] via-[#5b676f]/60 to-[#5b676f]/30" />
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pb-14 pt-40">
            <Link
              to="/"
              data-testid="ville-retour-accueil"
              className="inline-flex items-center gap-2 text-sm text-[#F9F9F9]/80 hover:text-[#D1BFAE] transition-colors duration-300"
            >
              <ArrowLeft size={15} strokeWidth={1.5} />
              Accueil
            </Link>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE, delay: 0.15 }}
              className="uppercase tracking-[0.25em] text-xs text-[#D1BFAE] mt-8"
            >
              {entree.mrc}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, ease: EASE, delay: 0.25 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4"
              data-testid="ville-titre"
            >
              Rénovation de salle de bain à{" "}
              <em className="text-[#D1BFAE]">{entree.nom}</em>
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE, delay: 0.45 }}
              className="mt-8 flex flex-wrap items-center gap-5"
            >
              <span className="text-xs uppercase tracking-[0.15em] text-[#C4C9CE]">
                RBQ : Q:5881-6240-01
              </span>
              <a
                data-testid="ville-phone-link"
                href="tel:4389851797"
                className="flex items-center gap-2 text-sm text-[#F9F9F9] hover:text-[#D1BFAE] transition-colors duration-300"
              >
                <Phone size={16} strokeWidth={1.5} />
                438-985-1797
              </a>
            </motion.div>
          </div>
        </section>

        <section className="py-28 md:py-36" data-testid="ville-contenu">
          <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 md:gap-16 items-center">
            <motion.div
              className="md:col-span-6"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.1, ease: EASE }}
            >
              <p className="uppercase tracking-[0.25em] text-xs text-[#D1BFAE]">
                Votre entrepreneur à {entree.nom}
              </p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight mt-6">
                Une salle de bain rénovée de A à Z,{" "}
                <em className="text-[#D1BFAE]">sans souci</em>
              </h2>
              <p className="text-[#C4C9CE] text-base sm:text-lg leading-relaxed mt-6">
                Vous cherchez un expert en rénovation de salle de bain à{" "}
                {entree.nom}? Salle de Bain Expert prend en charge votre projet
                complet — de la démolition à la dernière finition — avec une
                seule équipe et un seul engagement : votre satisfaction. Douche
                à l'italienne, céramique, bain autoportant, vanité quartz et
                plancher, nous livrons un résultat haut de gamme dans la
                région de {entree.nom} et partout dans la {entree.mrc}.
              </p>
              <ul className="mt-8 space-y-3">
                {SERVICES.map((s) => (
                  <li
                    key={s}
                    className="flex items-start gap-3 text-sm text-[#C4C9CE]"
                  >
                    <Check
                      size={15}
                      strokeWidth={1.5}
                      className="text-[#D1BFAE] mt-0.5 shrink-0"
                    />
                    {s}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              className="md:col-span-6"
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.2, ease: EASE }}
            >
              <div className="relative overflow-hidden aspect-[4/3] shadow-[0_8px_32px_rgba(0,0,0,0.4)] border border-white/10 group">
                <img
                  src={image2}
                  alt={`Salle de bain rénovée dans la région de ${entree.nom}`}
                  data-testid="ville-image"
                  className="w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 transition-[filter] duration-700"
                />
              </div>
            </motion.div>
          </div>
        </section>

        {voisines.length > 0 && (
          <section
            className="py-16 border-t border-white/5"
            data-testid="ville-voisines"
          >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
              <p className="uppercase tracking-[0.25em] text-xs text-[#D1BFAE]">
                Secteurs voisins desservis
              </p>
              <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
                {voisines.map((v) => (
                  <Link
                    key={v}
                    to={`/renovation-salle-de-bain/${slugify(v)}`}
                    data-testid={`ville-lien-${slugify(v)}`}
                    className="text-sm text-[#C4C9CE] hover:text-[#D1BFAE] transition-colors duration-300"
                  >
                    Rénovation salle de bain {v}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <QuoteForm />
      </main>
      <Footer />
    </>
  );
}
